import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, ArrowLeft, Globe } from 'lucide-react';

// Conditions d'utilisation — Régies par le droit québécois
// Bilingue FR/EN. Page publique, accessible sans authentification.

const CONTENT = {
  fr: {
    title: "Conditions d'utilisation",
    subtitle: 'En vigueur depuis février 2026',
    lastUpdate: 'Dernière mise à jour : 9 février 2026',
    sections: [
      {
        h: '1. Acceptation des conditions',
        p: [
          "En créant un compte ou en utilisant l'application ProPilote (ci-après \"l'Application\"), vous acceptez sans réserve les présentes conditions d'utilisation. Si vous n'acceptez pas ces conditions, n'utilisez pas l'Application.",
          "Ces conditions constituent un contrat légalement contraignant entre vous et Express Pat Morin Inc. (\"nous\", \"notre\").",
        ],
      },
      {
        h: '2. Description du service',
        p: [
          "ProPilote est une plateforme SaaS multi-tenant destinée aux professionnels du transport routier. Elle propose notamment :",
        ],
        list: [
          "La gestion des rapports journaliers de chauffeurs.",
          "Les inspections de véhicules conformes aux exigences SAAQ.",
          "La numérisation et l'archivage de bordereaux de transport (BOL).",
          "La signature électronique des livraisons.",
          "La communication entre chauffeurs et dispatchers.",
          "La facturation et la gestion comptable (modules administrateurs).",
          "Un mode hors-ligne avec synchronisation automatique.",
        ],
      },
      {
        h: '3. Compte utilisateur',
        p: [
          "Pour utiliser l'Application, vous devez disposer d'un compte fourni par votre employeur (transporteur) ou créé directement avec son autorisation. Vous êtes responsable :",
        ],
        list: [
          "De la confidentialité de vos identifiants.",
          "De toutes les activités effectuées depuis votre compte.",
          "De nous informer immédiatement de tout accès non autorisé.",
          "De fournir des informations exactes et à jour.",
        ],
      },
      {
        h: '4. Utilisation acceptable',
        p: [
          "Vous vous engagez à utiliser l'Application de manière conforme aux lois en vigueur et aux présentes conditions. Sont notamment interdits :",
        ],
        list: [
          "Toute tentative de contournement des mesures de sécurité.",
          "L'extraction massive de données par des moyens automatisés (scraping).",
          "La rétro-ingénierie ou la décompilation du code.",
          "L'utilisation de l'Application pour des activités illégales.",
          "La saisie volontaire d'informations fausses dans les rapports ou inspections.",
          "Le partage de votre compte avec un tiers.",
        ],
      },
      {
        h: '5. Propriété intellectuelle',
        p: [
          "L'Application, son code, ses interfaces, ses marques et tout son contenu sont la propriété exclusive d'Express Pat Morin Inc. et sont protégés par les lois canadiennes et internationales sur la propriété intellectuelle.",
          "Nous vous accordons une licence non exclusive, non transférable et révocable d'utiliser l'Application dans le cadre de votre activité professionnelle, conformément aux présentes conditions.",
        ],
      },
      {
        h: '6. Contenu utilisateur',
        p: [
          "Vous demeurez propriétaire des données que vous saisissez ou téléversez (rapports, BOL, photos, signatures). Vous nous accordez une licence mondiale, gratuite et non exclusive d'héberger, traiter et afficher ce contenu uniquement aux fins de la prestation du service.",
          "Vous garantissez que ces contenus n'enfreignent aucun droit de tiers ni aucune loi.",
        ],
      },
      {
        h: '7. Abonnement et paiement',
        p: [
          "Pour les administrateurs et entreprises, l'Application est offerte sur abonnement mensuel ou annuel. Les modalités tarifaires sont précisées au moment de la souscription.",
        ],
        list: [
          "Les paiements sont traités par Stripe Inc. (PCI-DSS).",
          "Les abonnements se renouvellent automatiquement sauf résiliation 30 jours avant l'échéance.",
          "Aucun remboursement n'est accordé pour les périodes déjà payées, sauf disposition légale contraire.",
          "Le défaut de paiement entraîne la suspension du service après préavis de 7 jours.",
          "L'utilisation par les chauffeurs est gratuite (incluse dans l'abonnement de leur employeur).",
        ],
      },
      {
        h: '8. Conformité SAAQ et responsabilité du conducteur',
        p: [
          "L'Application est un outil d'aide à la gestion. Elle ne remplace pas :",
        ],
        list: [
          "Le Règlement sur les heures de conduite et de repos (SAAQ).",
          "Le Code de la sécurité routière du Québec.",
          "Les règlements fédéraux sur le transport routier.",
          "Le jugement professionnel du conducteur.",
        ],
        p2: [
          "Le conducteur demeure entièrement responsable du respect des lois et règlements applicables, de l'exactitude des données saisies et de la tenue des registres officiels requis.",
        ],
      },
      {
        h: '9. Disponibilité et maintenance',
        p: [
          "Nous nous efforçons d'assurer une disponibilité de 99,5 % du service mais ne pouvons la garantir. Des opérations de maintenance peuvent occasionner des interruptions, généralement annoncées à l'avance.",
          "Le mode hors-ligne intégré permet la continuité d'utilisation pendant les interruptions de réseau ou de service.",
        ],
      },
      {
        h: '10. Limitation de responsabilité',
        p: [
          "Dans toute la mesure permise par la loi applicable :",
        ],
        list: [
          "Nous ne sommes pas responsables des dommages indirects, consécutifs ou immatériels (perte de profits, perte de données, atteinte à la réputation).",
          "Notre responsabilité totale envers vous, toutes causes confondues, est plafonnée au montant que vous (ou votre employeur) avez payé pour le service au cours des 12 mois précédant l'événement.",
          "Aucune disposition ne limite la responsabilité en cas de dol, faute lourde ou dommages corporels.",
        ],
      },
      {
        h: '11. Indemnisation',
        p: [
          "Vous acceptez d'indemniser Express Pat Morin Inc. contre toute réclamation de tiers résultant : (a) de votre violation des présentes conditions ; (b) de votre violation des droits d'un tiers ; (c) du contenu que vous avez fourni à l'Application.",
        ],
      },
      {
        h: '12. Résiliation',
        p: [
          "Vous pouvez fermer votre compte à tout moment depuis l'Application ou en contactant le support.",
          "Nous pouvons suspendre ou résilier votre compte avec un préavis raisonnable en cas de violation grave des présentes conditions, de non-paiement persistant, ou pour des raisons légales ou de sécurité impérieuses.",
          "Après résiliation, vos données sont conservées pendant 12 mois puis effacées, sous réserve des obligations légales de conservation.",
        ],
      },
      {
        h: '13. Modifications des conditions',
        p: [
          "Nous pouvons modifier les présentes conditions. Les modifications importantes seront notifiées au moins 30 jours avant leur entrée en vigueur, par courriel ou notification dans l'Application. Votre utilisation continue de l'Application après cette date vaut acceptation.",
        ],
      },
      {
        h: '14. Droit applicable et règlement des différends',
        p: [
          "Les présentes conditions sont régies par les lois de la province de Québec et les lois fédérales canadiennes applicables.",
          "Tout différend sera soumis à la compétence exclusive des tribunaux du district judiciaire de Drummond (Drummondville, Québec), sauf disposition impérative contraire.",
          "Avant toute action judiciaire, les parties s'engagent à tenter une médiation de bonne foi pendant 30 jours.",
        ],
      },
      {
        h: '15. Dispositions diverses',
        p: [
          "Si une clause de ces conditions est jugée invalide, les autres clauses restent en vigueur.",
          "Notre tolérance à un manquement ne vaut pas renonciation à invoquer ce manquement ultérieurement.",
          "Vous ne pouvez céder ces conditions sans notre accord écrit.",
        ],
      },
      {
        h: '16. Contact',
        p: [
          "Express Pat Morin Inc.",
          "Drummondville, Québec, Canada",
          "Courriel : apps@expresspatmorin.ca",
          "Téléphone : +1 (514) 444-2354",
          "Site : https://propilote.ca",
        ],
      },
    ],
  },
  en: {
    title: 'Terms of Use',
    subtitle: 'Effective February 2026',
    lastUpdate: 'Last updated: February 9, 2026',
    sections: [
      {
        h: '1. Acceptance of terms',
        p: [
          "By creating an account or using the ProPilote application (\"the App\"), you fully accept these Terms of Use. If you do not agree, do not use the App.",
          "These Terms constitute a legally binding agreement between you and Express Pat Morin Inc. (\"we\", \"our\").",
        ],
      },
      {
        h: '2. Service description',
        p: [
          "ProPilote is a multi-tenant SaaS platform for road transport professionals. It provides:",
        ],
        list: [
          "Driver daily report management.",
          "Vehicle inspections compliant with SAAQ requirements.",
          "Bill-of-Lading (BOL) scanning and archiving.",
          "Electronic delivery signatures.",
          "Driver–dispatcher communication.",
          "Billing and accounting (admin modules).",
          "Offline mode with automatic sync.",
        ],
      },
      {
        h: '3. User account',
        p: [
          "To use the App you need an account provided by your employer (carrier) or created with their authorization. You are responsible for:",
        ],
        list: [
          "Keeping your credentials confidential.",
          "All activities performed under your account.",
          "Notifying us immediately of any unauthorized access.",
          "Providing accurate and up-to-date information.",
        ],
      },
      {
        h: '4. Acceptable use',
        p: [
          "You agree to use the App in compliance with applicable laws and these Terms. The following are prohibited:",
        ],
        list: [
          "Any attempt to bypass security measures.",
          "Automated mass data extraction (scraping).",
          "Reverse-engineering or decompiling the code.",
          "Use of the App for illegal activities.",
          "Knowingly entering false information in reports or inspections.",
          "Sharing your account with a third party.",
        ],
      },
      {
        h: '5. Intellectual property',
        p: [
          "The App, its code, interfaces, trademarks and content are the exclusive property of Express Pat Morin Inc. and are protected by Canadian and international intellectual property laws.",
          "We grant you a non-exclusive, non-transferable, revocable license to use the App for your professional activity, subject to these Terms.",
        ],
      },
      {
        h: '6. User content',
        p: [
          "You retain ownership of the data you input or upload (reports, BOLs, photos, signatures). You grant us a worldwide, royalty-free, non-exclusive license to host, process and display this content solely for the purpose of providing the service.",
          "You warrant that this content does not infringe any third-party right or law.",
        ],
      },
      {
        h: '7. Subscription and payment',
        p: [
          "For administrators and businesses, the App is offered on monthly or annual subscription. Pricing is disclosed at signup.",
        ],
        list: [
          "Payments are processed by Stripe Inc. (PCI-DSS).",
          "Subscriptions auto-renew unless cancelled 30 days before renewal.",
          "No refunds are issued for already-paid periods, except where required by law.",
          "Failure to pay results in service suspension after 7-day notice.",
          "Driver use is free (included in their employer's subscription).",
        ],
      },
      {
        h: '8. SAAQ compliance and driver responsibility',
        p: [
          "The App is a management aid tool. It does not replace:",
        ],
        list: [
          "Hours of Service Regulation (SAAQ).",
          "Quebec Highway Safety Code.",
          "Federal road transport regulations.",
          "The driver's professional judgment.",
        ],
        p2: [
          "The driver remains fully responsible for compliance with applicable laws, the accuracy of entered data, and the keeping of officially required records.",
        ],
      },
      {
        h: '9. Availability and maintenance',
        p: [
          "We strive for 99.5% service availability but cannot guarantee it. Maintenance may cause interruptions, generally announced in advance.",
          "The built-in offline mode allows continued use during network or service interruptions.",
        ],
      },
      {
        h: '10. Limitation of liability',
        p: [
          "To the maximum extent permitted by applicable law:",
        ],
        list: [
          "We are not liable for indirect, consequential or intangible damages (lost profits, data loss, reputational harm).",
          "Our total liability to you, all causes combined, is capped at the amount you (or your employer) paid for the service in the 12 months preceding the event.",
          "Nothing limits liability in cases of fraud, gross negligence or bodily injury.",
        ],
      },
      {
        h: '11. Indemnification',
        p: [
          "You agree to indemnify Express Pat Morin Inc. against any third-party claim arising from: (a) your breach of these Terms; (b) your violation of a third party's rights; (c) content you submitted to the App.",
        ],
      },
      {
        h: '12. Termination',
        p: [
          "You may close your account at any time from within the App or by contacting support.",
          "We may suspend or terminate your account on reasonable notice for material breach, persistent non-payment, or compelling legal/security reasons.",
          "After termination, your data is retained for 12 months and then deleted, subject to legal retention obligations.",
        ],
      },
      {
        h: '13. Changes to terms',
        p: [
          "We may modify these Terms. Material changes will be notified at least 30 days before they take effect, by email or in-app notification. Continued use after that date constitutes acceptance.",
        ],
      },
      {
        h: '14. Governing law and dispute resolution',
        p: [
          "These Terms are governed by the laws of the Province of Quebec and applicable Canadian federal laws.",
          "Any dispute will be submitted to the exclusive jurisdiction of the courts of the Drummond judicial district (Drummondville, Quebec), unless otherwise required by mandatory law.",
          "Before any judicial action, the parties agree to attempt good-faith mediation for 30 days.",
        ],
      },
      {
        h: '15. Miscellaneous',
        p: [
          "If any clause is held invalid, the remaining clauses remain in force.",
          "Our tolerance of a breach does not waive our right to invoke that breach later.",
          "You may not assign these Terms without our written consent.",
        ],
      },
      {
        h: '16. Contact',
        p: [
          "Express Pat Morin Inc.",
          "Drummondville, Quebec, Canada",
          "Email: apps@expresspatmorin.ca",
          "Phone: +1 (514) 444-2354",
          "Web: https://propilote.ca",
        ],
      },
    ],
  },
};

export const TermsPage = () => {
  const navigate = useNavigate();
  const [lang, setLang] = useState('fr');
  const c = CONTENT[lang];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="terms-page">
      <div className="sticky top-0 z-10 bg-slate-950/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-800 rounded-lg"
            data-testid="terms-back-btn"
          >
            <ArrowLeft size={22} />
          </button>
          <FileText className="text-amber-500" size={24} />
          <h1 className="text-lg font-semibold flex-1 truncate">{c.title}</h1>
          <button
            onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm"
            data-testid="terms-lang-toggle"
          >
            <Globe size={16} />
            {lang === 'fr' ? 'EN' : 'FR'}
          </button>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <header className="space-y-2">
          <p className="text-amber-500 text-sm font-medium uppercase tracking-wider">
            {c.subtitle}
          </p>
          <h2 className="text-3xl font-bold">{c.title}</h2>
          <p className="text-slate-400 text-sm">{c.lastUpdate}</p>
        </header>

        {c.sections.map((s, i) => (
          <section key={i} className="space-y-3">
            <h3 className="text-xl font-semibold text-white">{s.h}</h3>
            {s.p?.map((para, j) => (
              <p key={j} className="text-slate-300 leading-relaxed">{para}</p>
            ))}
            {s.list && (
              <ul className="space-y-2 ml-4">
                {s.list.map((item, j) => (
                  <li key={j} className="text-slate-300 leading-relaxed list-disc list-outside ml-2">
                    {item}
                  </li>
                ))}
              </ul>
            )}
            {s.p2?.map((para, j) => (
              <p key={`p2-${j}`} className="text-slate-300 leading-relaxed">{para}</p>
            ))}
          </section>
        ))}

        <footer className="pt-8 border-t border-slate-800 text-slate-500 text-sm text-center">
          © 2026 Express Pat Morin Inc. — ProPilote
        </footer>
      </article>
    </div>
  );
};

export default TermsPage;
